import { ProtectedResource, ResourceAction } from 'types/action.enum';
import User from 'types/models/user';
import { UserPermission } from 'types/models/user-permission';
import { EntityKind } from 'types/models/user-preference';
import { upsertSafeChildProperties } from 'utils/Object/properties/upsert-child-property';

import { checkIsInternalAdmin } from './check-is-internal-admin';

/**
  * Special handling for IP Camera policy.
  *
  * For privacy reasons, internal admins by default can see IPCameraMapFlag on MapView but cannot view any
  * content on IPCameraListPage (where the thumbnails are displayed) or IPCameraDetailsPage.
  * This protects property owners' privacy.
 */
const getIPCameraPolicy = (
  userPermissions: UserPermission[],
  adminPolicyData: Record<ProtectedResource, ResourceAction[]>
): ResourceAction[] => {
  const isWhitelistedForIPCameras = userPermissions.some((permission) =>
    permission.entityKind === EntityKind.IP_CAMERA && permission.entityId === null
  );
  return isWhitelistedForIPCameras ? adminPolicyData[ProtectedResource.IP_CAMERA] : [];
};

export const parseInternalAccessPolicy = (
  user: User,
  adminPolicyData: Record<ProtectedResource, ResourceAction[]>,
  userPermissions: UserPermission[]
): User => {
  const isInternalAdmin = checkIsInternalAdmin(user);
  if (!isInternalAdmin) return user;

  const ipCameraPolicy = getIPCameraPolicy(userPermissions, adminPolicyData);

  const parsedUser = upsertSafeChildProperties(user, ['internalAccessPolicy'], {
    territories: user.permissions.accessCountries,
    authorizedActions: {
      ...adminPolicyData,
      [ProtectedResource.IP_CAMERA]: ipCameraPolicy
    }
  });
  return parsedUser;
};